import './brush-panel.less';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Tabs, Title } from '@meitu/saas-ui';
import { IBrushLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import { usePanelLayerId } from '@/hooks';
import { isEditAIPPTComponent, isEditAIPPTTemplate } from '@/hooks/fetch-template/editor-mode';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import PPTMarkPanel from '../ppt-mark-panel';
import { FillPointFeature } from './components/fill-point/fill-point';
import { FillFeature } from './components/fill/fill';
import { OpacityFeature } from './components/opacity/opacity';
import { RadiusFeature } from './components/radius/radius';
import { StrokeFeature } from './components/stroke/stroke';
import { StyleFeature } from './components/style/style';
import { WidthFeature } from './components/width/width';

const { SimpleTitle } = Title;

const BrushTitle: React.FC<{
    title: string;
    paddingTop?: number;
    border?: boolean;
    fontSize?: number;
}> = React.memo(({ title, border = false, paddingTop = 0, fontSize }) => {
    return (
        <div
            className={classNames('c-brush-panel__title', {
                'c-brush-panel__title-border': border,
            })}
            style={{ paddingTop }}
        >
            <SimpleTitle text={title} fontSize={fontSize} />
        </div>
    );
});

const pptMarkTabsList = [
    {
        name: '画笔',
        tabKey: 'bg',
    },
    {
        name: 'ppt打标',
        tabKey: 'pptMark',
    },
];

const RenderTabs: React.FC<{
    showMaskContent: boolean;
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
}> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);
    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const isAIPPTMark = isEditAIPPTComponent() || isEditAIPPTTemplate();
const BrushPanel = () => {
    const layerId = usePanelLayerId();
    const { $t } = useLocaleStore();
    const [showMaskContent, setShowMaskContent] = useState(false);
    if (!layerId) return null;
    const layer = editor.services.flatLayer.getLayer(layerId);
    if (!layer) return null;
    const { fills } = layer.data as IBrushLayerProps;
    const isArrowPen = editor.brushLayer.isArrowPen(layer);
    const isCurvePen = editor.brushLayer.isCurvePen(layer);
    const isWavyPen = editor.brushLayer.isWavyPen(layer);
    const isRect = editor.brushLayer.isRect(layer);
    const isPen = isArrowPen || isCurvePen || isWavyPen;

    return (
        <div className="c-brush-panel">
            {isAIPPTMark ? (
                <div style={{ marginTop: '16px' }}>
                    <RenderTabs
                        showMaskContent={showMaskContent}
                        setShowMaskContent={setShowMaskContent}
                        isPPT={isAIPPTMark}
                    />
                </div>
            ) : null}

            <div
                style={{
                    display: `${isAIPPTMark && showMaskContent ? 'none' : 'block'}`,
                }}
            >
                <HeaderToolbar id={layerId} editor={editor} style={{ padding: 16 }} />
                <BrushTitle
                    title={`${
                        isPen ? $t((v) => v.panel.brushPanel.brushPen) : $t((v) => v.panel.brushPanel.shape)
                    }${$t((v) => v.panel.brushPanel.style)}`}
                    fontSize={14}
                />
                {/* 画笔颜色 */}
                <FillFeature id={layerId} isGradient={!isArrowPen} />
                {/* 画笔属性 */}
                {isPen ? (
                    <>
                        {/* 画笔样式 */}
                        <StyleFeature id={layerId} isStroke={false} />
                        {/* 画笔粗细 */}
                        {isWavyPen ? null : <WidthFeature id={layerId} maxValue={100} minValue={1} isStroke={false} />}
                        {/* 箭头端点 */}
                        {isArrowPen ? <FillPointFeature id={layerId} /> : null}
                    </>
                ) : null}
                {/* 矩形圆角 */}
                {isRect ? <RadiusFeature id={layerId} /> : null}
                {/* 波浪线，箭头，多重画笔，没有描边 */}
                {!isArrowPen && !isWavyPen && fills.length === 1 ? (
                    <>
                        <DividerLine />
                        <BrushTitle title={$t((v) => v.panel.brushPanel.stroke)} fontSize={14} />
                        {/* 描边颜色 */}
                        <StrokeFeature id={layerId} />
                        {/* 描边样式 */}
                        <StyleFeature id={layerId} isStroke={true} />
                        {/* 描边宽度 */}
                        <WidthFeature id={layerId} maxValue={100} minValue={0} isStroke={true} />
                    </>
                ) : null}
                <DividerLine />
                {/* 图形透明度 */}
                <OpacityFeature id={layerId} />
                <DividerLine />
                {/* 通用功能 */}
                <BaseToolbar id={layerId} editor={editor} style={{ padding: '0 16px 16px' }} />{' '}
            </div>
            {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} key={layerId} /> : null}
        </div>
    );
};

export default React.memo(BrushPanel);
